import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import type { Resolver } from "react-hook-form";

import { createDefaultFormValues } from "@/core/helpers/assessment-default-values";
import { createAssessmentSchema } from "@/core/helpers/assessment-schema";
import type { Assessment, AssessmentFormValues } from "@/core/types";

import { ExclusiveOptionHandler } from "./ExclusiveOptionHandler";
import { FormLayout } from "./FormLayout";
import { QuestionRenderer } from "./QuestionRenderer";
import { ScrollToErrorHandler } from "./ScrollToErrorHandler";

type AssessmentRendererProps = {
  assessment: Assessment;
  isSubmitting?: boolean;
  onSubmit?: (data: AssessmentFormValues) => Promise<void> | void;
};

export function AssessmentRenderer({
  assessment,
  isSubmitting: isMutationPending = false,
  onSubmit,
}: AssessmentRendererProps) {
  // Extract elements from first page (single-page assessments only for now)
  const { elements } = assessment.definition.pages[0];

  const schema = createAssessmentSchema(elements);
  const defaultValues = createDefaultFormValues(elements);

  const methods = useForm<AssessmentFormValues>({
    defaultValues,
    mode: "onSubmit",
    // Cast resolver: zodResolver returns correct validation behavior at runtime,
    // but TypeScript can't infer the dynamic schema shape at compile time
    resolver: zodResolver(schema) as unknown as Resolver<AssessmentFormValues>,
  });

  const handleSubmit = async (data: AssessmentFormValues) => {
    if (onSubmit) {
      await onSubmit(data);
    } else {
      console.log("Assessment submitted:", data);
    }
  };

  const isSubmitting = methods.formState.isSubmitting || isMutationPending;

  return (
    <FormLayout
      description={assessment.definition.description}
      isDirty={methods.formState.isDirty}
      isSubmitting={isSubmitting}
      onSubmit={methods.handleSubmit(handleSubmit)}
      title={assessment.name}
    >
      <QuestionRenderer
        control={methods.control}
        errors={methods.formState.errors}
        questions={elements}
      />

      <ExclusiveOptionHandler
        control={methods.control}
        elements={elements}
        setValue={methods.setValue}
      />

      <ScrollToErrorHandler formState={methods.formState} />
    </FormLayout>
  );
}
